import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, ArrowUp, ArrowDown, MagnifyingGlass, Warning, Pencil, ChatCircle, CheckCircle, XCircle, FloppyDisk, DownloadSimple, FilePdf, Calendar, X } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';

const ONLY_TABS = [
  { v: '', label: 'All' },
  { v: 'duplicates', label: '⚠️ Duplicates', cls: 'bg-rose-50 border-rose-300 text-rose-800' },
  { v: 'unclaimed', label: 'Unclaimed' },
  { v: 'claimed', label: '✓ Claimed' },
  { v: 'suspense', label: '⏳ Suspense', cls: 'bg-amber-50 border-amber-300 text-amber-800' },
  { v: 'credits', label: '⬆ Credits' },
  { v: 'debits', label: '⬇ Debits' },
];

/* ─── Auto-remark rules (narration substring → suggested remark) ───
 * Rule: agar narration me ye pattern hai to remark inline input me auto-fill
 * ho jaye. User ek click se claim kar sakta hai. Confusion / multi-match
 * case me rules NOT applied (jaisa chal raha hai waisa rahega).
 */
const AUTO_REMARK_RULES = [
  { pattern: /555153.{6}5476/i, remark: 'RAVI RAJ 5476 COMMERCIAL CARD' },
];

function autoRemarkFor(narration, edited) {
  const text = `${narration || ''} ${edited || ''}`;
  const hits = AUTO_REMARK_RULES.filter(r => r.pattern.test(text));
  // Confusion case (>1 rule match) → no auto-fill, let user decide.
  if (hits.length === 1) return hits[0].remark;
  return null;
}

export default function BankStatementDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [txns, setTxns] = useState([]);
  const [only, setOnly] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = Newest First (default) | 'asc' = Oldest First
  const [loading, setLoading] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [queryTxn, setQueryTxn] = useState(null);
  const [remarks, setRemarks] = useState({}); // { [txnId]: string } – inline per-row remark
  const [claiming, setClaiming] = useState({}); // { [txnId]: bool } – disable while in-flight
  const [editNotesId, setEditNotesId] = useState('');   // txn id whose notes are being edited inline
  const [editNotesVal, setEditNotesVal] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const startNotesEdit = (t) => {
    setEditNotesId(t.id);
    setEditNotesVal(t.claim_notes || '');
  };
  const cancelNotesEdit = () => { setEditNotesId(''); setEditNotesVal(''); };
  const saveNotesEdit = async (t) => {
    if (savingNotes) return;
    setSavingNotes(true);
    try {
      const r = await api.patch(`/bank-statements/transactions/${t.id}/notes`, { notes: editNotesVal });
      toast.success('Remark updated · audit log saved');
      // Optimistic patch
      setTxns((curr) => curr.map((x) => x.id === t.id ? {
        ...x,
        claim_notes: editNotesVal.trim(),
        notes_edited_by_name: r.data?.notes_edited_by_name || (user?.name || ''),
        notes_edited_at: r.data?.notes_edited_at || new Date().toISOString(),
      } : x));
      cancelNotesEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save fail');
    } finally { setSavingNotes(false); }
  };

  const handleClaim = async (t) => {
    const note = (remarks[t.id] || '').trim();
    if (!note) {
      toast.error('Remark zaruri hai — kya transaction hai short me likhein');
      return;
    }
    setClaiming(c => ({ ...c, [t.id]: true }));
    try {
      await api.post(`/bank-statements/transactions/${t.id}/claim`, { notes: note });
      toast.success('✅ Claimed & locked');
      // Optimistic UI: row turant claimed dikhe, blank/focus-ring na rahe
      setTxns(curr => curr.map(r => r.id === t.id ? {
        ...r, claimed: true, claim_notes: note,
        claimed_by_name: user?.name || 'You',
        claimed_at: new Date().toISOString(),
      } : r));
      setRemarks(r => { const n = { ...r }; delete n[t.id]; return n; });
      // Blur any focused element so the blue ring chala jaaye
      try { document.activeElement && document.activeElement.blur && document.activeElement.blur(); } catch { /* empty */ }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Claim fail'); }
    finally { setClaiming(c => { const n = { ...c }; delete n[t.id]; return n; }); }
  };

  const handleUnclaim = async (t) => {
    if (!window.confirm('Is transaction ko unclaim karna hai?')) return;
    setClaiming(c => ({ ...c, [t.id]: true }));
    try {
      await api.post(`/bank-statements/transactions/${t.id}/unclaim`);
      toast.success('Unclaimed — fresh status pe wapas');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Unclaim fail'); }
    finally { setClaiming(c => { const n = { ...c }; delete n[t.id]; return n; }); }
  };

  const handleDownload = async (format = 'xlsx') => {
    try {
      const res = await api.get(`/bank-statements/${id}/download`, {
        responseType: 'blob',
        params: {
          format,
          only: only || undefined,
          q: q || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          order: sortOrder,
        },
      });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const fname = m ? m[1] : `statement_${id}.${format}`;
      const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`📥 ${fname}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Download fail'); }
  };

  // Quick date preset helpers
  const applyPreset = (k) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (k === 'today') { setDateFrom(fmt(today)); setDateTo(fmt(today)); }
    else if (k === 'week') {
      const d = new Date(today); d.setDate(d.getDate() - d.getDay()); // Sun
      setDateFrom(fmt(d)); setDateTo(fmt(today));
    } else if (k === 'month') {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(fmt(d)); setDateTo(fmt(today));
    } else if (k === 'last_month') {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      setDateFrom(fmt(start)); setDateTo(fmt(end));
    } else if (k === 'clear') {
      setDateFrom(''); setDateTo('');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        only: only || undefined,
        q: q || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        order: sortOrder,
      };
      const [{ data: m }, { data: ds }, { data: ts }] = await Promise.all([
        api.get('/bank-statements/access/me'),
        api.get(`/bank-statements/${id}/dashboard`),
        api.get(`/bank-statements/${id}/transactions`, { params }),
      ]);
      setMeta(m); setStats(ds); setTxns(ts || []);
      // Auto-fill inline remark for matching unclaimed rows. User-typed values preserved.
      setRemarks(prev => {
        const next = { ...prev };
        for (const t of (ts || [])) {
          if (t.claimed || t.is_duplicate) continue;
          if (next[t.id] !== undefined && next[t.id] !== '') continue; // user already typed
          const auto = autoRemarkFor(t.narration, t.edited_narration);
          if (auto) next[t.id] = auto;
        }
        return next;
      });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Load fail'); }
    finally { setLoading(false); }
  }, [id, only, q, dateFrom, dateTo, sortOrder]);

  useEffect(() => { load(); }, [load]);

  if (!meta) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="bs-detail-page">
      <button onClick={() => nav('/bank-statements')} className="text-sm text-violet-600 hover:underline flex items-center gap-1" data-testid="bs-back-btn"><ArrowLeft size={14} /> Back to statements</button>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">📑 Statement Detail</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDownload('xlsx')}
            className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-1 shadow-sm"
            title="Download Excel with all remarks/claims preserved"
            data-testid="bs-download-btn"
          ><DownloadSimple size={14} weight="bold" /> Excel</button>
          <button
            onClick={() => handleDownload('pdf')}
            className="text-xs px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white font-bold flex items-center gap-1 shadow-sm"
            title="Download PDF report (printable)"
            data-testid="bs-download-pdf-btn"
          ><FilePdf size={14} weight="bold" /> PDF</button>
          <div className="text-xs text-gray-500">As role: <b>{user?.role}</b> · {meta.can_edit ? 'Edit allowed' : 'View only'}</div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2.5">
          {[
            { k: 'Total', v: stats.total, c: 'bg-gray-100 text-gray-800' },
            { k: 'Credits ₹', v: `₹${Math.round(stats.total_credit).toLocaleString('en-IN')}`, c: 'bg-emerald-100 text-emerald-800' },
            { k: 'Debits ₹', v: `₹${Math.round(stats.total_debit).toLocaleString('en-IN')}`, c: 'bg-rose-100 text-rose-800' },
            { k: '⚠️ Duplicates', v: stats.duplicates, c: 'bg-rose-50 text-rose-700 border border-rose-200' },
            { k: 'Unclaimed CR', v: stats.unclaimed_credits, c: 'bg-amber-100 text-amber-800' },
            { k: 'Claimed', v: stats.claimed, c: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
            { k: '⏳ Suspense', v: stats.suspense, c: 'bg-amber-50 text-amber-800 border border-amber-200' },
          ].map(s => (
            <div key={s.k} className={`p-2.5 rounded-lg ${s.c} text-center`} data-testid={`bs-stat-${s.k}`}>
              <div className="text-[10px] font-bold uppercase">{s.k}</div>
              <div className="text-base font-bold">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        <div className="flex gap-1 flex-wrap">
          {ONLY_TABS.map(t => (
            <button key={t.v} onClick={() => setOnly(t.v)} className={`text-xs px-2.5 py-1 rounded border transition-colors ${only === t.v ? 'bg-violet-600 text-white border-violet-600' : (t.cls || 'bg-white border-gray-300 hover:bg-gray-50')}`} data-testid={`bs-tab-${t.v || 'all'}`}>
              {t.label}
            </button>
          ))}
          <button
            onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
            className={`text-xs px-2.5 py-1 rounded border transition-colors flex items-center gap-1 font-bold ${sortOrder === 'desc' ? 'bg-violet-50 text-violet-700 border-violet-300 hover:bg-violet-100' : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'}`}
            title={sortOrder === 'desc' ? 'Currently: Newest First (click to switch to Oldest First)' : 'Currently: Oldest First (click to switch to Newest First)'}
            data-testid="bs-sort-order-toggle"
          >
            {sortOrder === 'desc' ? (
              <><ArrowDown size={12} weight="bold" /> Newest First</>
            ) : (
              <><ArrowUp size={12} weight="bold" /> Oldest First</>
            )}
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <MagnifyingGlass size={14} className="text-gray-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="search narration / ref no…" className="h-8 text-xs w-64" data-testid="bs-search" />
        </div>
      </div>

      {/* Date Filter row — presets + custom range */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
        <Calendar size={14} className="text-violet-600" weight="bold" />
        <span className="text-xs font-bold text-gray-700">Date Range:</span>
        {[
          { k: 'today', label: 'Today' },
          { k: 'week', label: 'This Week' },
          { k: 'month', label: 'This Month' },
          { k: 'last_month', label: 'Last Month' },
        ].map(p => (
          <button key={p.k} onClick={() => applyPreset(p.k)} className="text-[11px] px-2 py-0.5 rounded border border-gray-300 hover:bg-violet-50 hover:border-violet-300 transition-colors" data-testid={`bs-preset-${p.k}`}>
            {p.label}
          </button>
        ))}
        <span className="text-[10px] text-gray-400 mx-1">|</span>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-gray-500">From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-[11px] border border-gray-300 rounded px-1.5" data-testid="bs-date-from" />
          <span className="text-[10px] text-gray-500">To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-[11px] border border-gray-300 rounded px-1.5" data-testid="bs-date-to" />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => applyPreset('clear')} className="text-[11px] px-2 py-0.5 rounded text-rose-600 hover:bg-rose-50 flex items-center gap-0.5" data-testid="bs-date-clear">
            <X size={12} /> Clear
          </button>
        )}
        {(dateFrom || dateTo) && (
          <span className="ml-auto text-[10px] bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5 font-bold">
            {dateFrom || '—'} → {dateTo || '—'} · {txns.length} matching
          </span>
        )}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead className="min-w-[420px]">Narration</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead>Ref / UTR</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead>Claim</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8">Loading…</TableCell></TableRow> :
               txns.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">Koi transaction nahi mila is filter pe.</TableCell></TableRow> :
               txns.map(t => (
                <TableRow key={t.id} className={t.is_duplicate ? 'bg-rose-50/50' : (t.suspense ? 'bg-amber-50/40' : '')} data-testid={`bs-txn-${t.id}`}>
                  <TableCell className="text-[11px] whitespace-nowrap">{t.date ? new Date(t.date).toLocaleDateString('en-IN') : '-'}</TableCell>
                  <TableCell className="text-xs">
                    <div className="whitespace-pre-wrap break-words">{t.narration}</div>
                    {t.edited_narration && <div className="text-emerald-700 font-medium whitespace-pre-wrap break-words">✎ {t.edited_narration}</div>}
                    {t.suspense_reason && <div className="text-[10px] text-amber-700 mt-0.5">⏳ {t.suspense_reason}</div>}
                    {t.agency_suggestions?.length > 0 && !t.claimed && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {t.agency_suggestions.slice(0, 3).map((s, i) => (
                          <span key={i} className="text-[9px] bg-violet-50 text-violet-700 border border-violet-200 rounded px-1 py-0.5" title={s.reason}>
                            🎯 {s.agency_name || s.user_name || 'Match'} ({s.score}%)
                          </span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs font-bold text-rose-700">{t.debit > 0 ? `₹${Math.round(t.debit).toLocaleString('en-IN')}` : '-'}</TableCell>
                  <TableCell className="text-right text-xs font-bold text-emerald-700">{t.credit > 0 ? `₹${Math.round(t.credit).toLocaleString('en-IN')}` : '-'}</TableCell>
                  <TableCell className="text-[10px] font-mono text-gray-700 break-all" title={t.ref_no}>{t.ref_no || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {t.is_duplicate && <span className="text-[9px] font-bold bg-rose-200 text-rose-900 rounded px-1 py-0.5" title={`${t.duplicate_reason} · ${t.duplicate_score}%`}><Warning size={9} className="inline mr-0.5" />DUPLICATE</span>}
                      {t.suspense && <span className="text-[9px] font-bold bg-amber-200 text-amber-900 rounded px-1 py-0.5">SUSPENSE</span>}
                      {t.open_queries_count > 0 && <span className="text-[9px] font-bold bg-blue-200 text-blue-900 rounded px-1 py-0.5">{t.open_queries_count} OPEN Q</span>}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-[260px]">
                    {t.claimed ? (
                      <div className="text-[11px] space-y-0.5">
                        <div className="font-bold text-emerald-700 flex items-center gap-1"><CheckCircle size={12} weight="fill" />{t.claim_agency_name || t.claim_user_name || 'Claimed'}</div>
                        {editNotesId === t.id ? (
                          <div className="flex gap-1 items-stretch mt-1">
                            <Input
                              value={editNotesVal}
                              onChange={(e) => setEditNotesVal(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveNotesEdit(t);
                                if (e.key === 'Escape') cancelNotesEdit();
                              }}
                              className="h-7 text-[11px] flex-1"
                              autoFocus
                              disabled={savingNotes}
                              placeholder="Remark…"
                              data-testid={`bs-notes-edit-input-${t.id}`}
                            />
                            <button
                              onClick={() => saveNotesEdit(t)}
                              disabled={savingNotes}
                              className="px-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded text-[10px] font-bold flex items-center gap-0.5"
                              title="Save (Enter)"
                              data-testid={`bs-notes-save-${t.id}`}
                            ><FloppyDisk size={11} weight="fill" /></button>
                            <button
                              onClick={cancelNotesEdit}
                              disabled={savingNotes}
                              className="px-2 border border-gray-300 hover:bg-gray-100 rounded text-[10px] font-bold"
                              title="Cancel (Esc)"
                              data-testid={`bs-notes-cancel-${t.id}`}
                            >✕</button>
                          </div>
                        ) : (
                          <div className="flex items-start gap-1 group">
                            <div className="flex-1 text-gray-700 whitespace-pre-wrap break-words">
                              📝 {t.claim_notes || <span className="italic text-gray-400">(no remark)</span>}
                            </div>
                            {meta.can_edit && (
                              <button
                                onClick={() => startNotesEdit(t)}
                                className="p-0.5 text-indigo-600 hover:bg-indigo-50 rounded opacity-60 group-hover:opacity-100"
                                title="Edit remark"
                                data-testid={`bs-notes-edit-${t.id}`}
                              ><Pencil size={11} /></button>
                            )}
                          </div>
                        )}
                        <div className="text-[10px] text-gray-500">by {t.claimed_by_name}{t.claimed_at ? ` · ${new Date(t.claimed_at).toLocaleDateString('en-IN')}` : ''}</div>
                        {t.notes_edited_by_name && t.notes_edited_at && (
                          <div className="text-[10px] text-indigo-600" title={`Remark edited on ${new Date(t.notes_edited_at).toLocaleString('en-IN')}`}>
                            ✎ edited by {t.notes_edited_by_name} · {new Date(t.notes_edited_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                          </div>
                        )}
                      </div>
                    ) : t.is_duplicate ? (
                      <span className="text-[10px] text-rose-600 italic">duplicate — claim blocked</span>
                    ) : (
                      <div className="flex gap-1 items-stretch">
                        <Input
                          value={remarks[t.id] || ''}
                          onChange={e => setRemarks(r => ({ ...r, [t.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleClaim(t); }}
                          placeholder="Remark / agency name…"
                          className="h-7 text-[11px] flex-1"
                          disabled={!!claiming[t.id]}
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck={false}
                          data-testid={`bs-remark-${t.id}`}
                        />
                        <button
                          onClick={() => handleClaim(t)}
                          disabled={!!claiming[t.id] || !(remarks[t.id] || '').trim()}
                          className="px-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded text-[10px] font-bold flex items-center gap-0.5"
                          title="Claim with this remark"
                          data-testid={`bs-claim-${t.id}`}
                        ><CheckCircle size={12} weight="fill" />Claim</button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-0.5">
                      {t.claimed && meta.is_admin && (
                        <button onClick={() => handleUnclaim(t)} disabled={!!claiming[t.id]} className="p-1 text-rose-600 hover:bg-rose-50 rounded disabled:opacity-50" title="Unclaim (admin)" data-testid={`bs-unclaim-${t.id}`}><XCircle size={14} /></button>
                      )}
                      {meta.can_edit && (
                        <button onClick={() => setEditTxn(t)} className="p-1 text-violet-600 hover:bg-violet-50 rounded" title="Edit narration / suspense" data-testid={`bs-edit-${t.id}`}><Pencil size={14} /></button>
                      )}
                      <button onClick={() => setQueryTxn(t)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Queries" data-testid={`bs-query-${t.id}`}><ChatCircle size={14} /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editTxn && <EditDialog txn={editTxn} onClose={() => setEditTxn(null)} onSaved={load} />}
      {queryTxn && <QueryDialog txn={queryTxn} canResolve={meta.can_edit} onClose={() => setQueryTxn(null)} onChanged={load} />}
    </div>
  );
}


/* ─── Edit (CA): narration override + suspense flag ─── */
function EditDialog({ txn, onClose, onSaved }) {
  const [edited, setEdited] = useState(txn.edited_narration || '');
  const [suspense, setSuspense] = useState(txn.suspense || false);
  const [reason, setReason] = useState(txn.suspense_reason || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/bank-statements/transactions/${txn.id}`, {
        edited_narration: edited, suspense, suspense_reason: reason,
      });
      toast.success('Saved');
      onClose(); onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save fail'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bs-edit-dialog">
        <DialogHeader><DialogTitle>✎ Edit Transaction</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-gray-50 border rounded p-2">
            <div><b>Original:</b> {txn.narration}</div>
            <div className="mt-1"><b>Amount:</b> ₹{Math.round((txn.credit || txn.debit) || 0).toLocaleString('en-IN')} {txn.credit > 0 ? '(CR)' : '(DR)'}</div>
          </div>
          <div>
            <label className="text-xs font-semibold">Edited / Annotated narration</label>
            <Input value={edited} onChange={e => setEdited(e.target.value)} placeholder="e.g., KISHORI INDANE 4-Apr booking payment" className="h-9 text-sm mt-1" data-testid="bs-edit-narr" />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
            <input type="checkbox" checked={suspense} onChange={e => setSuspense(e.target.checked)} data-testid="bs-suspense-toggle" />
            ⏳ Mark as Suspense (kya hai pata nahi / pending verification)
          </label>
          {suspense && <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for suspense" className="h-9 text-sm" data-testid="bs-suspense-reason" />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="bs-edit-save"><FloppyDisk size={14} className="mr-1" />Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Queries thread ─── */
function QueryDialog({ txn, canResolve, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/bank-statements/transactions/${txn.id}/queries`); setList(data || []); }
    catch (err) { /* empty */ }
  }, [txn.id]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/bank-statements/transactions/${txn.id}/queries`, { text: text.trim() });
      toast.success('Query bheji — admin/assistant ko notify ho gaya');
      setText(''); load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Fail'); }
    finally { setBusy(false); }
  };

  const resolve = async (qid) => {
    try { await api.post(`/bank-statements/transactions/${txn.id}/queries/${qid}/resolve`); toast.success('Resolved'); load(); onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Fail'); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bs-query-dialog">
        <DialogHeader><DialogTitle>💬 Queries — {txn.narration?.slice(0, 50)}…</DialogTitle></DialogHeader>
        <div className="max-h-72 overflow-y-auto border rounded bg-gray-50 p-2 space-y-2">
          {list.length === 0 ? <div className="text-center text-xs text-gray-500 py-4">Koi query nahi hai abhi tak.</div> :
           list.map(q => (
            <div key={q.id} className={`text-xs p-2 rounded ${q.is_reply ? 'bg-blue-50 border border-blue-200 ml-6' : 'bg-white border border-gray-200'}`}>
              <div className="font-bold flex items-center justify-between">
                <span>{q.by_name} <span className="text-[10px] font-normal text-gray-500">({q.by_role})</span></span>
                <span className="text-[10px] text-gray-400">{formatDateTime(q.created_at)}</span>
              </div>
              <div className="mt-1 whitespace-pre-wrap">{q.text}</div>
              {!q.is_reply && !q.resolved && canResolve && (
                <button onClick={() => resolve(q.id)} className="mt-1 text-[10px] text-emerald-700 hover:underline">✓ Resolve</button>
              )}
              {q.resolved && <span className="text-[10px] text-emerald-600 ml-2">✓ resolved</span>}
            </div>
          ))}
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Write a query or reply…" className="w-full text-xs border rounded p-2 min-h-[60px]" data-testid="bs-query-text" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
          <Button onClick={send} disabled={busy || !text.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="bs-query-send">{busy ? 'Sending…' : 'Send'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
